import React, {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Link from 'next/link';
import { PaginationItem } from '@mui/material';
import { ProductJsonLd, ProductJsonLdProps } from 'next-seo';
import { serialize } from 'corporate-utils';
import { useRouter } from 'next/router';
import { useMediaQuery, useTheme } from '@mui/material';

import CardOtherProducts from '../../../components/cards/card-other-products/CardOtherProducts';
import Markdown from '../../../components/markdown/Markdown';
import MobileFilters from '../../../components/mobile-filters/mobile-filters-component/MobileFilters';
import MobileFiltersModal from '../../../components/mobile-filters/mobile-filters-modal/MobileFiltersModal';
import PageSelect from '../../../components/page-select/PageSelect';
import Pagination from '../../../components//pagination/Pagination';
import Repeater from '../../../components//repeater/Repeater';
import Sidebar from '../../../components//sidebar/Sidebar';
import { Category, Section } from '../../../models/domain/interfaces';
import { Pages } from '../../../models/domain/enum';
import { getAvailableStringFromCategoryByLocale } from '../../../utils/categories';
import { getCategoriesByParentCategoryIdUI } from '../../../services/dwh';
import { Logger } from 'corporate-utils';

import {
  Container,
  InfoGenericWrapperStyled,
  Label,
  Left,
  Main,
  SeoText,
  MainPagination,
  MainRepeater,
  MarkdownWrapper,
  Results,
  StyledContentWrapper,
  Tag,
  Tags,
  TitleMain,
  TitleWithSortingWrapperStyled,
} from './Content.style';
import SortingFilters from '../../../components/sorting-filters/SortingFilters';
import { HideOnDesktop, HideOnMobile } from '../../../styles/shared';
import { viewSearchResults } from '../../../utils/tagManager';
import { SubcategorySeo } from '../../../models/auto-generated-strapi-types/types/components/SubcategorySeo';
import { CategoryTexts } from '../../../models/auto-generated-strapi-types/types/components/CategoryTexts';
import ContactForm from '../../contacts/form/Form';
import { UserContext } from '../../../models/interface';
interface IProps {
  data: {
    sidebar: {
      sections: Section[];
      filterSections: Section[];
    };
    productSearched?: string;
    productSchemas?: ProductJsonLdProps[];
    products: [
      {
        category: string;
        name: string;
        image: string;
        link: string;
        categoriesCalculatedInfo: { name: string; href: string }[][];
      }
    ];
    categoryTags: {
      text: string;
      id?: number;
      href: string;
    }[];
    pagination: {
      page: number;
      pageSize: number;
      totalItems: number;
    };
    type?: Pages;
    subcategorySeo?: SubcategorySeo;
    categoryTexts?: CategoryTexts;
    originalUrl?: string;
    contactForm?: {
      title: string;
      description: string;
      titleFontSize?: number;
      titleMobileFontSize?: number;
      titleFontWeight?: number;
      titleMobileFontWeight?: number;
      descriptionMobileFontWeight?: number;
      descriptionFontWeight?: number;
      descriptionMobileFontSize?: number;
      descriptionFontSize?: number;
    };
    removeContactForm: boolean;
  };
  translations: any;
  userContext: UserContext;
  isPrerenderRequest: boolean;
}

const Content: React.FC<IProps> = ({
  data,
  translations,
  userContext,
  isPrerenderRequest,
}: IProps) => {
  const {
    categoryTags,
    pagination,
    productSchemas,
    productSearched,
    products,
    sidebar,
    type: pageType,
    subcategorySeo,
    categoryTexts,
    originalUrl,
    contactForm,
    removeContactForm,
  } = data || {};

  const fromUrlToSlug = (url: string) => {
    const urlSplitted = url.split('?');
    const uri = urlSplitted[0].split?.('/')?.filter?.((value) => value !== '');
    const queryParams = urlSplitted?.[1] ? urlSplitted[1].split('&') : [];

    const slug: any = {
      slug: uri,
    };

    queryParams.forEach((queryParam) => {
      const valueSplitted = queryParam.split('=');

      const isEncoded = decodeURI(valueSplitted[0]) !== valueSplitted[0];
      const isEncodedComponent =
        decodeURIComponent(valueSplitted[0]) !== valueSplitted[0];

      const key =
        isEncodedComponent && !isEncoded
          ? decodeURIComponent(valueSplitted[0])
          : isEncoded
            ? decodeURI(valueSplitted[0])
            : valueSplitted[0];

      const isEncodedValue = decodeURI(valueSplitted[1]) !== valueSplitted[1];
      const isEncodedComponentValue =
        decodeURIComponent(valueSplitted[1]) !== valueSplitted[1];

      const value =
        isEncodedComponentValue && !isEncodedValue
          ? decodeURIComponent(valueSplitted[0])
          : isEncodedValue
            ? decodeURI(valueSplitted[1])
            : valueSplitted[1];

      slug[key] = value;
    });

    return slug;
  };

  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [isFull, setIsFull] = useState(
    (sidebar?.sections && sidebar?.sections.length > 1).toString()
  );
  const [page, setPage] = useState(pagination?.page || 1);
  const [pageSize, setPageSize] = useState(pagination?.pageSize || 24);
  const [categoryDownloaded, setCategoryDownloaded] =
    useState<Record<string, Category[]>>();
  const router = useRouter();
  const { query, locale } = router;
  const [isSubcategory, setIsSubcategory] = useState<boolean>(() => {
    const generatedQuery =
      originalUrl !== '' ? fromUrlToSlug((originalUrl as string) || '') : query;

    const currentSubcategoryId = Number(
      generatedQuery?.[translations?.queryParmas?.subcategory]
    );

    if (!Number.isNaN(currentSubcategoryId)) {
      return true;
    } else {
      return false;
    }
  });

  const [texts, setTexts] = useState<Omit<CategoryTexts, 'id'>>(
    isSubcategory
      ? {
        description: subcategorySeo?.categoryTexts?.description || '',
        productsOptionsDescription:
            subcategorySeo?.categoryTexts?.productsOptionsDescription || '',
        productsOptionsTitle:
            subcategorySeo?.categoryTexts?.productsOptionsTitle || '',
        seoText: subcategorySeo?.categoryTexts?.seoText || '',
        subtitle: subcategorySeo?.categoryTexts?.subtitle || '',
      }
      : {
        description: categoryTexts?.description || '',
        productsOptionsDescription:
            categoryTexts?.productsOptionsDescription || '',
        productsOptionsTitle: categoryTexts?.productsOptionsTitle || '',
        seoText: categoryTexts?.seoText || '',
        subtitle: categoryTexts?.subtitle || '',
      }
  );

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('md'));

  const [, setRerender] = useState<number>(0); // Trick to force re-render

  useEffect(() => {
    if (isSubcategory) {
      setTexts({
        description: subcategorySeo?.categoryTexts?.description || '',
        productsOptionsDescription:
          subcategorySeo?.categoryTexts?.productsOptionsDescription || '',
        productsOptionsTitle:
          subcategorySeo?.categoryTexts?.productsOptionsTitle || '',
        seoText: subcategorySeo?.categoryTexts?.seoText || '',
        subtitle: subcategorySeo?.categoryTexts?.subtitle || '',
      });
    } else {
      setTexts({
        description: categoryTexts?.description || '',
        productsOptionsDescription:
          categoryTexts?.productsOptionsDescription || '',
        productsOptionsTitle: categoryTexts?.productsOptionsTitle || '',
        seoText: categoryTexts?.seoText || '',
        subtitle: categoryTexts?.subtitle || '',
      });
    }
  }, [isSubcategory, subcategorySeo, categoryTexts]);

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  useEffect(() => {
    setIsFull(
      (!(
        (typeof window === 'undefined' || isDesktop) &&
        sidebar?.sections &&
        sidebar?.sections.length > 1
      )).toString()
    );
  }, [isDesktop, sidebar?.sections]);

  const rootCategoryId = useMemo(() => {
    const { id } =
      (categoryTags &&
        categoryTags.length > 0 &&
        categoryTags[
          (categoryTags?.length > 0 ? categoryTags?.length : 1) - 1
        ]) ||
      {};
    return id ?? 0;
  }, [categoryTags]);

  const defaultContactForm = {
    title: translations?.pages?.contactForm?.header || '',
    description: '',
  };

  const rootSection = sidebar?.sections?.[(sidebar?.sections?.length || 1) - 1];

  const onChangePageSize = (size: number): void => {
    setPageSize(~~size);
    setPage(1);
    router.push(createPagePath(1, ~~size));
  };

  const onChangePage = (page: number): void => {
    setPage(~~page);
    router.push(createPagePath(~~page));
  };

  const createPagePath = (pageToChange: number, size?: number): string => {
    const { query, asPath } = router;
    const path = asPath.split('?')[0];
    const queryCopy = {
      ...query,
    };

    delete queryCopy?.slug;
    // delete queryCopy?.q;

    const newQuery: any = {
      ...queryCopy,
      page: pageToChange.toString(),
    };

    if (size !== undefined && size !== null) {
      newQuery.pageSize = size;
    }

    return `/${locale}${path}?${serialize(newQuery)}`;
  };

  const downloadAllCategories = useCallback(
    async ({ voices } = {}) => {
      if (voices) {
        const categories: Category[][] = await Promise.all(
          voices.flatMap(async ({ id }: { id?: number }) => {
            if (id)
              return (
                categoryDownloaded?.[id] ||
                (await getCategoriesByParentCategoryIdUI(`${id}`, true, locale))
              );
            else return [];
          })
        );

        if (categories && categoryDownloaded) setCategoryDownloaded(undefined);

        const categoryToDownload = categories.reduce((acc, items) => {
          const father = items?.find?.(
            (category: any) => category?.depth === 0
          );
          if (father?.id && !acc?.[father?.id]) {
            acc[father.id] = items.slice(0, items.length);
          }
          return acc;
        }, {} as Record<string, Category[]>);

        setCategoryDownloaded(categoryToDownload);
      }
    },
    [categoryDownloaded, locale]
  );

  useEffect(() => {
    downloadAllCategories({
      voices: rootSection?.voices || [],
    }).catch(Logger.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootSection?.title]);

  const subcategoriesTags = useMemo(() => {
    const generatedQuery =
      originalUrl !== '' ? fromUrlToSlug((originalUrl as string) || '') : query;

    const currentSubcategoryId = Number(
      generatedQuery?.[translations?.queryParmas?.subcategory]
    );

    if (!Number.isNaN(currentSubcategoryId)) {
      setIsSubcategory(true);
      let rootCategory = categoryDownloaded?.[rootCategoryId];
      let currentSubcategory: any;

      if (pageType === Pages.SECTOR) {
        Object.keys(
          (categoryDownloaded as Record<string, Category[]>) || {}
        ).forEach((key: string) => {
          const categories = categoryDownloaded?.[key];
          const result = categories?.find?.((category: any) => {
            return category?.id === currentSubcategoryId;
          });

          if (result) {
            rootCategory = categoryDownloaded?.[key];
            currentSubcategory = result;
          }
        });
      } else {
        currentSubcategory = rootCategory?.find?.(
          (category) => category?.id === currentSubcategoryId
        );
      }

      const limit = pageType === Pages.SECTOR ? 0 : 1;

      const tags: { name: string; id: number }[] = [];
      while (currentSubcategory && currentSubcategory.depth >= limit) {
        tags.push({
          name:
            currentSubcategory &&
            getAvailableStringFromCategoryByLocale(
              currentSubcategory,
              locale,
              'packages/corporate-ui/src/sections/category/content/Content.tsx'
            ),
          id: currentSubcategory?.id,
        });
        currentSubcategory = rootCategory?.find?.(
          (category) => category?.id === currentSubcategory?.idFather
        );
      }

      return tags.reverse();
    }
  }, [
    originalUrl,
    query,
    translations?.queryParmas?.subcategory,
    categoryDownloaded,
    rootCategoryId,
    pageType,
    locale,
  ]);

  useEffect(() => {
    if (!query.page) {
      setPage(1);
    }
  }, [query?.page]);

  useEffect(() => {
    if (productSearched) {
      viewSearchResults(productSearched);
    }
  }, [productSearched]);

  return (
    <StyledContentWrapper>
      <Container keepSpace={productSearched}>
        <HideOnMobile>
          {sidebar?.sections && sidebar?.sections.length > 0 ? (
            <Left>
              <Sidebar sections={sidebar?.sections} />
            </Left>
          ) : (
            <></>
          )}
        </HideOnMobile>

        <Main full={isFull}>
          {texts?.subtitle && (
            <TitleMain noSpace={productSearched}>{texts.subtitle}</TitleMain>
          )}
          {texts?.description && (
            <MarkdownWrapper>
              <Markdown>{texts.description}</Markdown>
            </MarkdownWrapper>
          )}

          <HideOnMobile>
            <TitleWithSortingWrapperStyled>
              <TitleMain noSpace={productSearched}>
                {(productSearched
                  ? translations?.pages?.category?.main?.products
                    ?.productSearched +
                    ': ' +
                    productSearched
                  : texts?.productsOptionsTitle) ||
                  translations?.pages?.category?.main?.products?.title ||
                  ''}
              </TitleMain>
              {!productSearched ? (
                <SortingFilters
                  handleFilterButtonClick={() => setShowFiltersModal(true)}
                  translations={translations}
                  filterSections={sidebar?.filterSections || []}
                />
              ) : (
                <></>
              )}
            </TitleWithSortingWrapperStyled>
            {texts?.productsOptionsDescription && (
              <MarkdownWrapper>
                <Markdown>{texts.productsOptionsDescription}</Markdown>
              </MarkdownWrapper>
            )}
          </HideOnMobile>
          <HideOnDesktop>
            {productSearched && (
              <TitleMain noSpace={productSearched}>
                {translations?.pages?.category?.main?.products
                  ?.productSearched +
                  ': ' +
                  productSearched}
              </TitleMain>
            )}
          </HideOnDesktop>

          <HideOnDesktop>
            {texts?.productsOptionsDescription && (
              <MarkdownWrapper>
                <Markdown>{texts.productsOptionsDescription}</Markdown>
              </MarkdownWrapper>
            )}
            <MobileFilters
              sections={sidebar?.sections}
              handleFilterButtonClick={async () => {
                await downloadAllCategories({
                  voices: rootSection?.voices || [],
                }).catch(Logger.error);
                setTimeout(() => {
                  setShowFiltersModal(true);
                }, 10);
              }}
              translations={translations}
              filterSections={sidebar?.filterSections || []}
            />
          </HideOnDesktop>

          <InfoGenericWrapperStyled>
            {((categoryTags && categoryTags.length) ||
              (subcategoriesTags && subcategoriesTags.length)) && (
              <Tags>
                {React.Children.toArray(
                  categoryTags?.map((tag, index) =>
                    tag && tag.text !== '' ? (
                      (subcategoriesTags && subcategoriesTags.length > 0) ||
                      index !==
                        categoryTags.filter((t) => t.text).length - 1 ? (
                          <Link
                            href={tag?.href || ''}
                            passHref
                            key={`tag_1_${index}`}
                          >
                            <Tag>{tag?.text}</Tag>
                          </Link>
                        ) : (
                          <Tag disabled>{tag?.text}</Tag>
                        )
                    ) : (
                      <></>
                    )
                  )
                )}
                {subcategoriesTags &&
                  categoryTags &&
                  Children.toArray(
                    subcategoriesTags?.map((tag, index) =>
                      tag ? (
                        tag.id !==
                        Number(query?.[translations.urls.subcategory]) ? (
                            <Link
                              href={`${
                                categoryTags[categoryTags.length - 1]?.href
                              }/?${translations?.queryParmas?.subcategory}=${
                                tag.id
                              }`}
                              passHref
                              key={`tag_2_${index}`}
                            >
                              <Tag>{tag?.name}</Tag>
                            </Link>
                          ) : (
                            <Tag disabled>{tag?.name}</Tag>
                          )
                      ) : (
                        <></>
                      )
                    )
                  )}
              </Tags>
            )}
            <Results>
              {pagination?.totalItems || 0}{' '}
              {translations?.pages?.category?.main?.filterMenu?.results}
            </Results>
          </InfoGenericWrapperStyled>
          <MainRepeater>
            {products?.length ? (
              <Repeater
                spacing={5}
                cols={{ xs: 12, md: 6, lg: 4, xl: 3 }}
                data={products.filter((product) => {
                  return !product.link.match(/([^:]\/)\/+/g);
                })}
                props={{ translations, isPrerenderRequest }}
                component={CardOtherProducts}
              />
            ) : router.query.slug?.includes(
              translations?.urls?.searchProduct
            ) ? (
                <p>
                  {translations?.pages?.category?.main?.products?.emptyResults}
                </p>
              ) : (
                <p>{translations?.pages?.category?.main?.products?.empty}</p>
              )}
          </MainRepeater>
          {products?.length > 0 && (
            <MainPagination>
              <Pagination
                count={Math.ceil(pagination?.totalItems / pageSize)}
                page={page}
                onChangePage={onChangePage}
                renderItem={(item) => {
                  return (
                    <PaginationItem
                      {...item}
                      href={
                        (!item?.selected &&
                          !item?.disabled &&
                          item?.page &&
                          createPagePath(item.page)) ||
                        null
                      }
                      onClick={(e: any) => {
                        e.preventDefault();
                        item.onClick();
                      }}
                      component={
                        (!item.selected && !item.disabled && 'a') || null
                      }
                    />
                  );
                }}
              ></Pagination>
              <HideOnMobile>
                <div>
                  <Label>
                    {
                      translations?.pages?.category?.main?.products?.pagination
                        ?.label
                    }
                  </Label>
                  <PageSelect
                    onChangePageSize={(val: number) => onChangePageSize(val)}
                    currentValue={pageSize}
                  />
                </div>
              </HideOnMobile>
              {productSchemas?.map((product, index) => (
                <ProductJsonLd
                  {...product}
                  keyOverride={product?.sku}
                  key={`productJsonLd_1_${index}`}
                />
              ))}
            </MainPagination>
          )}
          {products?.length > 0 && texts?.seoText && (
            <SeoText>
              <Markdown>{texts.seoText}</Markdown>
            </SeoText>
          )}
          {!removeContactForm && (
            <ContactForm
              data={contactForm || defaultContactForm}
              translations={translations}
              userContext={userContext}
            />
          )}
        </Main>
      </Container>
      <HideOnDesktop>
        {categoryDownloaded && (
          <MobileFiltersModal
            show={showFiltersModal}
            onClose={() => setShowFiltersModal(false)}
            sections={categoryDownloaded}
            translations={translations}
            rootSection={rootSection?.title}
            pageType={pageType as Pages}
          />
        )}
      </HideOnDesktop>
    </StyledContentWrapper>
  );
};

export default Content;
